export type TrainingModule = {
  title: string;
  hours: string;
  text: string;
};

export type TrainingProgram = {
  slug: string;
  index: string;
  title: string;
  tagline: string;
  duration: string;
  rhythm: string;
  level: string;
  groupSize: string;
  certification: string;
  image: string;
  modules: TrainingModule[];
  outcomes: string[];
};

export const trainingPrograms: TrainingProgram[] = [
  {
    slug: 'soudure-multi-procedes',
    index: '01',
    title: 'Soudure multi-procédés',
    tagline:
      "TIG, MIG/MAG et ARC sur acier carbone et inox : maîtriser le geste, comprendre le paramètre, produire un cordon conforme.",
    duration: '4 semaines · 160 h',
    rhythm: 'Temps plein, 8 h – 16 h',
    level: 'Débutant à confirmé',
    groupSize: '8 participants maximum',
    certification: 'Attestation de compétences + carnet de soudeur',
    image:
      'https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.39.37-1.jpeg',
    modules: [
      {
        title: 'Sécurité au poste de soudage',
        hours: '8 h',
        text: "Risques électriques, fumées, rayonnements, incendie. EPI, ventilation, consignation du poste et conduite à tenir en cas d'incident.",
      },
      {
        title: 'Théorie des procédés',
        hours: '14 h',
        text: "Principe du TIG, du MIG/MAG et de l'électrode enrobée. Choix du gaz, polarité, intensité, vitesse et préparation des bords.",
      },
      {
        title: 'Atelier TIG sur acier et inox',
        hours: '48 h',
        text: "Réglage, amorçage, cordons en position plane puis verticale, purge argon sur inox et lecture des défauts sur éprouvette.",
      },
      {
        title: 'Atelier MIG/MAG et ARC',
        hours: '48 h',
        text: "Réglages synergiques, angulation de la torche, racinage et remplissage, reprise de cordons sur tôles de 3 à 10 mm.",
      },
      {
        title: 'Essais et contrôle des soudures',
        hours: '24 h',
        text: "Contrôle visuel, ressuage, essai de pliage et interprétation des défauts. Constitution du carnet de soudeur.",
      },
      {
        title: 'Évaluation finale sur ouvrage',
        hours: '18 h',
        text: "Réalisation d'un assemblage complet noté sur la géométrie, l'étanchéité, l'esthétique et le respect du mode opératoire.",
      },
    ],
    outcomes: [
      'Régler un poste TIG, MIG/MAG ou ARC selon le matériau',
      'Produire un cordon sain en position plane et verticale',
      'Identifier et corriger les défauts courants de soudure',
      'Travailler en sécurité sur un poste de soudage',
    ],
  },
  {
    slug: 'chaudronnerie-tracage',
    index: '02',
    title: 'Chaudronnerie & traçage',
    tagline:
      "Du plan au volume : tracer, découper, rouler et assembler une virole, un fond ou une trémie conforme au relevé.",
    duration: '6 semaines · 240 h',
    rhythm: 'Temps plein, 8 h – 16 h',
    level: 'Initiation et perfectionnement',
    groupSize: '8 participants maximum',
    certification: 'Attestation de compétences en chaudronnerie',
    image:
      'https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.05.26-1.jpeg',
    modules: [
      {
        title: 'Lecture de plans et cotation',
        hours: '30 h',
        text: "Vues, coupes, symboles de soudure, tolérances et débits. Passage d'un plan d'ensemble aux éléments à fabriquer.",
      },
      {
        title: 'Traçage et développés',
        hours: '46 h',
        text: "Développé de cylindre, de cône et de trémie. Calcul des longueurs, épaisseurs et jeux de soudage.",
      },
      {
        title: 'Découpe et mise en forme',
        hours: '60 h',
        text: "Découpe plasma, cisaille, pliage et roulage. Réglage des machines, contrôle du rayon et gestion du retour élastique.",
      },
      {
        title: 'Assemblage et maintien',
        hours: '56 h',
        text: "Pointage, gabarits, ordre de montage et contrôle dimensionnel. Prévention des déformations par séquencement.",
      },
      {
        title: 'Chaudronnerie inox et finitions',
        hours: '30 h',
        text: "Sensibilisation à l'inox, décapage et passivation, finitions visibles et préparation avant peinture.",
      },
      {
        title: 'Projet d’atelier',
        hours: '18 h',
        text: "Réalisation encadrée d'un ensemble chaudronné : traçage, fabrication, contrôle et présentation au formateur.",
      },
    ],
    outcomes: [
      'Lire un plan de chaudronnerie et en extraire les débits',
      'Tracer et développer un ensemble simple',
      'Régler les machines de découpe et de roulage',
      'Contrôler la géométrie d’un ouvrage assemblé',
    ],
  },
  {
    slug: 'tuyauterie-industrielle',
    index: '03',
    title: 'Tuyauterie industrielle',
    tagline:
      "Isométriques, prefabrication et montage : construire un réseau qui tient la pression, la pente et le supportage.",
    duration: '5 semaines · 200 h',
    rhythm: 'Temps plein, 8 h – 16 h',
    level: 'Initiation et perfectionnement',
    groupSize: '8 participants maximum',
    certification: 'Attestation de compétences en tuyauterie',
    image:
      'https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.05.27.jpeg',
    modules: [
      {
        title: 'Plans, isométriques et repérage',
        hours: '32 h',
        text: "Lecture d'isométrique, nomenclature, repérage des tronçons et préparation d'un dossier de prefabrication.",
      },
      {
        title: 'Préfabrication en atelier',
        hours: '56 h',
        text: "Coupe, chanfrein, alignement, pointage et soudage de tronçons. Tolérances de faux équerrage et contrôle des cotes.",
      },
      {
        title: 'Raccordements et dilatation',
        hours: '34 h',
        text: "Brides, raccords, lyres de dilatation, pentes et points de purge. Comprendre les efforts sur la tuyauterie.",
      },
      {
        title: 'Supportage et montage sur site',
        hours: '40 h',
        text: "Types de supports, calage, alignement, serrage et reprise de cotes sur site. Sécurité du travail en hauteur.",
      },
      {
        title: 'Essais et mise en service',
        hours: '24 h',
        text: "Essai hydraulique et pneumatique, contrôle d'étanchéité, rinçage, purge et préparation de la réception.",
      },
      {
        title: 'Évaluation sur réseau témoin',
        hours: '14 h',
        text: "Montage noté d'un réseau complet à partir d'un isométrique, avec contrôle dimensionnel et essai.",
      },
    ],
    outcomes: [
      'Lire et exploiter un isométrique de tuyauterie',
      'Préfabriquer un tronçon conforme aux tolérances',
      'Poser un supportage et respecter les pentes',
      'Réaliser un essai d’étanchéité et documenter le résultat',
    ],
  },
  {
    slug: 'montage-echafaudage',
    index: '04',
    title: 'Montage & échafaudage',
    tagline:
      "Travail en hauteur, stabilité et protections collectives : monter, contrôler et démonter sans improviser.",
    duration: '1 semaine · 40 h',
    rhythm: 'Temps plein, 8 h – 16 h',
    level: 'Tous niveaux · obligatoire avant site',
    groupSize: '10 participants maximum',
    certification: 'Attestation de montage / démontage d’échafaudage',
    image:
      'https://sgsctic.com/wp-content/uploads/2026/01/Echafaudeur-0284_ERANIAN_MG_0991.png',
    modules: [
      {
        title: 'Cadre réglementaire et risques',
        hours: '6 h',
        text: "Obligations de l'employeur et du monteur, analyse de risques, coactivité et balisage de la zone de travail.",
      },
      {
        title: 'Vérification du matériel',
        hours: '6 h',
        text: "Contrôle des tubes, planchers, colliers et stabilisateurs. Identification du matériel refusé et traçabilité.",
      },
      {
        title: 'Montage et stabilité',
        hours: '14 h',
        text: "Implantation, assise, ancrages, contreventement et descentes de charge. Exercices de montage sur structure réelle.",
      },
      {
        title: 'Protections et travail en hauteur',
        hours: '8 h',
        text: "Garde-corps, filets, harnais et points d'ancrage. Déplacement sécurisé et évacuation.",
      },
      {
        title: 'Réception et démontage',
        hours: '6 h',
        text: "Fiche de vérification, étiquetage, ordre de démontage et rangement du matériel.",
      },
    ],
    outcomes: [
      'Vérifier un lot d’échafaudage avant montage',
      'Monter une structure stable et contreventée',
      'Mettre en place les protections collectives',
      'Renseigner la fiche de réception et étiqueter l’ouvrage',
    ],
  },
  {
    slug: 'lecture-plans-hse',
    index: '05',
    title: 'Lecture de plans & HSE',
    tagline:
      "La base commune des métiers de l'industrie : comprendre un plan, préparer une intervention, travailler sans accident.",
    duration: '2 semaines · 80 h',
    rhythm: 'Temps plein, 8 h – 16 h',
    level: 'Débutant · prérequis aux autres parcours',
    groupSize: '12 participants maximum',
    certification: 'Attestation de formation professionnelle',
    image:
      'https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2026-01-06-at-13.50.19.jpeg',
    modules: [
      {
        title: 'Dessin technique et symboles',
        hours: '20 h',
        text: "Vues, échelles, cotations, symboles de soudure et de tuyauterie. Lecture croisée plan d'ensemble / détail.",
      },
      {
        title: 'Métrologie et contrôle',
        hours: '16 h',
        text: "Utilisation du mètre, du pied à coulisse, de l'équerre et du niveau. Tolérances et relevé de cotes.",
      },
      {
        title: 'Préparation d’intervention',
        hours: '18 h',
        text: "Analyse de risques, plan de prévention, permis de travail et briefing d'équipe avant démarrage.",
      },
      {
        title: 'EPI et gestes sûrs',
        hours: '14 h',
        text: "Équipements de protection, manutention manuelle, consignation et réaction aux situations d'urgence.",
      },
      {
        title: 'Mise en situation',
        hours: '12 h',
        text: "Préparation complète d'une intervention fictive, présentée au formateur et évaluée en groupe.",
      },
    ],
    outcomes: [
      'Décoder un plan technique et ses symboles',
      'Prendre des cotes fiables avec les bons instruments',
      'Préparer une intervention avec son analyse de risques',
      'Appliquer les règles HSE de base sur un site',
    ],
  },
];

export const trainingSteps = [
  {
    number: '01',
    title: 'Dossier de candidature',
    text: "Le formulaire d'inscription nous donne votre parcours, le parcours visé et vos disponibilités.",
  },
  {
    number: '02',
    title: 'Entretien de positionnement',
    text: "Un échange court en atelier permet de vérifier le niveau réel et d'orienter vers le parcours adapté.",
  },
  {
    number: '03',
    title: 'Convention et planning',
    text: "Vous recevez le programme détaillé, les dates de session et la convention de formation.",
  },
  {
    number: '04',
    title: 'Session en atelier',
    text: "Théorie le matin, pratique l'après-midi, évaluation en fin de parcours et remise de l'attestation.",
  },
];

export const trainingFigures = [
  { value: 5, suffix: '', label: 'Parcours techniques', hint: 'De la soudure à l’échafaudage' },
  { value: 8, suffix: '', label: 'Stagiaires par groupe', hint: 'Encadrement rapproché' },
  { value: 70, suffix: '%', label: 'De temps d’atelier', hint: 'Le geste avant la théorie' },
  { value: 3, suffix: '', label: 'Procédés de soudure', hint: 'TIG · MIG/MAG · ARC' },
];

export const syntferPartner = {
  name: 'Syntfer',
  label: 'Partenaire technique',
  sector: 'Industries & structures métalliques',
  url: 'https://syntfer.com/',
  monogram: 'SY',
};

export const referenceSectors = [
  {
    title: 'Agro-industrie',
    icon: 'ri-plant-line',
    scope: 'Cuves inox, tuyauterie process',
    zone: 'Douala',
  },
  {
    title: 'Énergie',
    icon: 'ri-flashlight-line',
    scope: 'Réseaux vapeur, supportage',
    zone: 'Bonabéri',
  },
  {
    title: 'Cimenterie',
    icon: 'ri-building-4-line',
    scope: 'Charpentes, montage en arrêt',
    zone: 'Littoral',
  },
  {
    title: 'Logistique portuaire',
    icon: 'ri-ship-line',
    scope: 'Portiques, anticorrosion',
    zone: 'Port de Douala',
  },
  {
    title: 'Mines & carrières',
    icon: 'ri-roadster-line',
    scope: 'Silos, chaudronnerie lourde',
    zone: 'Région du Centre',
  },
  {
    title: 'Industries manufacturières',
    icon: 'ri-settings-5-line',
    scope: 'Lignes process, tuyauterie',
    zone: 'Bassa',
  },
];

